import React, { useRef, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { getLabels, formatTimestampFromDB } from './DashboardComponent';

const color = '#f39c12';
const submenuOptions = ['1s', '5s', '1m', '5m'];

function getGsmSignalText(value) {
  if (value === null || value === undefined) return '-';
  if (value >= -50 && value <= 0) return 'EXCELLENT';
  if (value >= -70 && value < -50) return 'GOOD';
  if (value >= -80 && value < -70) return 'WEAK';
  if (value < -80) return 'VERY WEAK';
  return '-';
}

function GsmChart({ data, last, interval: propInterval, onIntervalChange }) {
  const [interval, setIntervalValue] = useState(propInterval || '1s');
  const chartRef = useRef();
  const sortedData = [...data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const chartData = React.useMemo(() => ({
    labels: getLabels(sortedData, interval),
    datasets: [{
      label: 'GSM Signal dBm',
      data: sortedData.map(d => d.gsm_signal),
      borderColor: color,
      backgroundColor: 'rgba(255, 205, 86, 0.1)',
      pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? color : 'rgba(255,205,86,0.5)'),
      pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 8 : 3),
      pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 3 : 1),
    }],
  }), [sortedData, interval]);

  useEffect(() => {
    if (chartRef.current && chartRef.current.chartInstance) {
      const chart = chartRef.current.chartInstance;
      chart.data.labels = chartData.labels;
      chart.data.datasets[0].data = chartData.datasets[0].data;
      chart.update('none');
    }
  }, [chartData]);

  // Sincronizează intervalul cu prop-ul
  useEffect(() => {
    if (propInterval && propInterval !== interval) {
      setIntervalValue(propInterval);
    }
  }, [propInterval, interval]);

  // LIVE dacă ultima valoare e în ultimele 10 minute
  const isLive = last && (Date.now() - new Date(last.created_at).getTime() < 10 * 60 * 1000);
  const lastValue = last ? `${last.gsm_signal} dBm (${getGsmSignalText(last.gsm_signal)})` : '-';
  const lastTime = last ? formatTimestampFromDB(last.created_at) : '-';

  // Calculează axa Y dinamică
  const values = sortedData.map(d => d.gsm_signal).filter(v => typeof v === 'number');
  let minY = 0, maxY = 100;
  if (values.length) {
    minY = Math.floor(Math.min(...values) - 0.1 * Math.abs(Math.min(...values)));
    maxY = Math.ceil(Math.max(...values) + 0.1 * Math.abs(Math.max(...values)));
    if (minY === maxY) { minY -= 5; maxY += 5; }
  }

  const handleIntervalChange = async (opt) => {
    setIntervalValue(opt);
    if (onIntervalChange) {
      onIntervalChange('gsm', opt);
    }
  };

  return (
    <div style={{ marginBottom: 0, height: 160, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
      <div style={{ display: 'flex', alignItems: 'center', fontWeight: 500, fontSize: 12, marginBottom: 2, color, textTransform: 'uppercase', letterSpacing: 1, gap: 7, flexWrap: 'wrap' }}>
        <span style={{fontSize: 12}}>GSM SIGNAL</span>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#fff', background: color, borderRadius: 8, padding: '1px 7px', boxShadow: `0 0 8px ${color}55` }}>{lastValue}</span>
        {isLive && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, marginLeft: 6 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#27ae60', animation: 'pulse 1s infinite' }} />
            <span style={{ fontWeight: 600, color: '#27ae60', fontSize: 11 }}>LIVE</span>
          </span>
        )}
        {submenuOptions.map(opt => (
          <button
            key={opt}
            onClick={() => handleIntervalChange(opt)}
            style={{
              padding: '1px 7px',
              borderRadius: 14,
              border: interval === opt ? '2px solid #f39c12' : '1px solid #bbb',
              background: interval === opt ? '#fffbe9' : '#fff',
              color: interval === opt ? '#f39c12' : '#222',
              fontWeight: interval === opt ? 'bold' : 'normal',
              fontSize: 10,
              cursor: 'pointer',
              boxShadow: interval === opt ? '0 0 0 2px #f39c1255' : '0 1px 2px #eee',
              transition: 'all 0.2s',
              outline: interval === opt ? 'none' : undefined,
              marginLeft: 3
            }}
            tabIndex={-1}
            title="Perioada de colectare"
          >
            {opt}
          </button>
        ))}
        <span style={{ fontSize: 10, color: '#888', marginLeft: 6, fontWeight: 400 }}>{lastTime}</span>
      </div>
      <div style={{ height: 220 }}>
        <Line ref={chartRef} data={chartData} options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label + ': ' + context.parsed.y;
                  return label;
                }
              }
            }
          },
          scales: {
            y: {
              min: minY,
              max: maxY,
              ticks: {
                stepSize: Math.ceil((maxY-minY)/5),
                font: { size: 13 },
                padding: 8
              },
              grid: {
                color: '#eee',
                lineWidth: 1.5,
                drawTicks: true
              }
            },
            x: {
              grid: {
                color: '#f5f5f5',
                lineWidth: 1
              },
              ticks: {
                font: { size: 11 },
                padding: 6
              }
            }
          }
        }} />
      </div>
    </div>
  );
}

export default GsmChart; 